import React, { useState } from "react";
import { format } from "date-fns";
import { fr, de, it, enGB } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";
import { CalendarDays, Clock } from "lucide-react";

const LOCALES = { fr, de, it, en: enGB };

// Themed date + time picker producing a "YYYY-MM-DDTHH:mm" string (drop-in for datetime-local).
function parse(val) {
  if (!val) return { date: undefined, time: "18:00" };
  const [d, t] = val.split("T");
  const [y, m, day] = d.split("-").map(Number);
  return { date: new Date(y, m - 1, day), time: (t || "18:00").slice(0, 5) };
}
function build(date, time) {
  if (!date) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${time || "00:00"}`;
}

export default function DateTimeField({ value, onChange, testId }) {
  const { lang } = useLang();
  const { date, time } = parse(value);
  const [open, setOpen] = useState(false);
  const loc = LOCALES[lang] || fr;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="w-full flex items-center gap-2 bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-left text-stone-200 hover:border-amber-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
          <CalendarDays className="w-4 h-4 text-amber-500 shrink-0" />
          {date ? <span>{format(date, "dd MMM yyyy", { locale: loc })} · <span className="text-amber-300">{time}</span></span> : <span className="text-stone-500">—</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-[#121418] border-[#272A34] text-stone-200">
        <Calendar mode="single" locale={loc} selected={date} onSelect={(d) => onChange(build(d, time))} initialFocus />
        <div className="flex items-center gap-2 p-3 border-t border-[#272A34]">
          <Clock className="w-4 h-4 text-amber-500" />
          <input type="time" data-testid={`${testId}-time`} value={time} onChange={(e) => onChange(build(date || new Date(), e.target.value))}
            className="bg-[#0B0C0E] border border-[#272A34] rounded-md px-2 py-1.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
